/**
 * =============================================================================
 * Componente: ReferralProgramFormModal
 * Versão: 1.1.0 (Consolidação Configurações)
 *
 * Descrição:
 *   Modal de criação/edição de programas de indicação.
 *   - Mantém o slug único por tenant (validação no client + unicidade no banco).
 *   - Suporta os 3 tipos: público, privado e híbrido.
 *   - Permite definir tipo de recompensa padrão e auto-aprovação de afiliados.
 *
 * v1.1.0 — Consolidação:
 *   A antiga página dedicada `/tenant/indicacao/configuracoes` foi removida.
 *   Seus campos (terms_html, require_terms_acceptance) passaram a ser editados
 *   aqui, organizados em duas abas:
 *     1) "Geral" — todos os campos originais (regras, recompensa, CRM, ativo).
 *     2) "Termos" — modelo de contrato vinculado e exigência de aceite.
 *
 * v1.2.0 — Centralização do branding:
 *   A personalização visual (cor + logo) do painel/landing pública do
 *   afiliado foi consolidada em Configurações › Indicação (fonte única
 *   por tenant). Os campos `brand_primary_color` e `brand_logo_url`
 *   foram removidos desta tela; valores legados em `settings` permanecem
 *   inertes no JSONB sem impacto funcional.
 * =============================================================================
 */
import { useEffect, useMemo, useState } from 'react';
// v1.4.0 (2026-05-02) — Migrado para FormModalShell compartilhado
// (mesmo padrão premium usado nos modais de Tarefas e Campanhas).
import { Eye, Info, FileText, Award, Plus, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
// Shell premium compartilhado (header com ícone + abas + footer fixo).
import { FormModalShell } from '@/components/shared/FormModalShell';
import { type ModalTabConfig } from '@/components/shared/ModalTabsBar';

import { useToast } from '@/hooks/use-toast';
import {
  useReferralProgramMutations,
  type AffiliateProgram,
} from '@/hooks/useReferralPrograms';
import { useContractTemplates } from '@/hooks/useContractTemplates';
import { ProgramTermsDialog } from './ProgramTermsDialog';
import type { ProgramTermsData } from '@/hooks/useProgramTermsForAffiliate';
import {
  ReferralCrmIntegrationSection,
  type ReferralCrmFormData,
} from './ReferralCrmIntegrationSection';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  program?: AffiliateProgram | null;
}

/** Gera um slug a partir do nome (lowercase, sem acento, com hífens). */
const slugify = (text: string): string =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 60);

interface FormState {
  name: string;
  slug: string;
  description: string;
  program_type: 'open' | 'invite_only' | 'internal';
  default_reward_type: 'percentage' | 'fixed' | 'points' | 'tiered' | 'recurring';
  default_reward_value: string;
  cookie_duration_days: string;
  min_payout_amount: string;
  auto_approve_affiliates: boolean;
  auto_approve_commissions: boolean;
  is_active: boolean;
  // ----- Aba "Termos" (v1.1.0 / v1.2.0) -----
  terms_html: string;
  /**
   * v2.1.0 — ID do modelo de contrato (contract_templates) vinculado como
   * Termos do Programa. '' = nenhum (mantém fallback de terms_html).
   */
  terms_template_id: string;
  require_terms_acceptance: boolean;
  // Preserva o restante de `settings` (campos não editados aqui, incluindo
  // chaves legadas de branding que agora vivem em Configurações › Indicação)
  settings_extra: Record<string, any>;
  // ----- Integração CRM (Etapa 4) -----
  crm: ReferralCrmFormData;
}

const emptyState: FormState = {
  name: '',
  slug: '',
  description: '',
  program_type: 'invite_only',
  default_reward_type: 'percentage',
  default_reward_value: '10',
  cookie_duration_days: '90',
  min_payout_amount: '50',
  auto_approve_affiliates: false,
  auto_approve_commissions: false,
  is_active: true,
  terms_html: '',
  terms_template_id: '',
  require_terms_acceptance: true,
  settings_extra: {},
  crm: {
    auto_create_contact: true,
    auto_create_deal: false,
    crm_workspace_id: null,
    crm_pipeline_id: null,
    crm_phase_id: null,
    crm_source: null,
    crm_default_seller_id: null,
    crm_distribution_queue_id: null,
    crm_default_tags: [],
    crm_deal_title_template: null,
    crm_assignment_mode: null, // v1.3.0
  },
};

// v1.3.0 — Configuração das abas do modal (consumida pelo ModalTabsBar).
const PROGRAM_MODAL_TABS: ModalTabConfig<'general' | 'branding'>[] = [
  { id: 'general', label: 'Geral', icon: Info },
  { id: 'branding', label: 'Termos', icon: FileText },
];

export function ReferralProgramFormModal({ open, onOpenChange, program }: Props) {
  const { toast } = useToast();
  const { createMutation, updateMutation } = useReferralProgramMutations();
  const [form, setForm] = useState<FormState>(emptyState);
  const [tab, setTab] = useState<'general' | 'branding'>('general');
  // v2.1.0 — controle do dialog de pré-visualização dos termos
  const [previewOpen, setPreviewOpen] = useState(false);
  const { templates: contractTemplates = [], isLoading: loadingTemplates } =
    useContractTemplates();

  // Hidrata o estado quando abrir em modo edição
  useEffect(() => {
    if (!open) return;
    setTab('general');
    if (program) {
      // Preserva o JSONB `settings` integralmente. Chaves legadas de branding
      // (`brand_primary_color`, `brand_logo_url`) ficam inertes em settings_extra
      // — não são mais editáveis (centralizado em Configurações › Indicação).
      const rawSettings = ((program as any).settings ?? {}) as Record<string, any>;

      setForm({
        name: program.name ?? '',
        slug: program.slug ?? '',
        description: program.description ?? '',
        program_type: program.program_type as FormState['program_type'],
        default_reward_type:
          (program.default_reward_type as FormState['default_reward_type']) ??
          'percentage',
        default_reward_value: String(program.default_reward_value ?? 0),
        cookie_duration_days: String(program.cookie_duration_days ?? 90),
        min_payout_amount: String(program.min_payout_amount ?? 0),
        auto_approve_affiliates: !!program.auto_approve_affiliates,
        auto_approve_commissions: !!program.auto_approve_commissions,
        is_active: !!program.is_active,
        terms_html: (program as any).terms_html ?? '',
        terms_template_id: (program as any).terms_template_id ?? '',
        require_terms_acceptance:
          (program as any).require_terms_acceptance ?? true,
        settings_extra: rawSettings ?? {},
        crm: {
          auto_create_contact: true, // sempre true (forçado por segurança)
          auto_create_deal: (program as any).auto_create_deal ?? false,
          crm_workspace_id: (program as any).crm_workspace_id ?? null,
          crm_pipeline_id: (program as any).crm_pipeline_id ?? null,
          crm_phase_id: (program as any).crm_phase_id ?? null,
          crm_source: (program as any).crm_source ?? null,
          crm_default_seller_id: (program as any).crm_default_seller_id ?? null,
          crm_distribution_queue_id:
            (program as any).crm_distribution_queue_id ?? null,
          crm_default_tags: (program as any).crm_default_tags ?? [],
          crm_deal_title_template: (program as any).crm_deal_title_template ?? null,
          // v1.3.0 — modo persistido (fixed_seller | queue | null)
          crm_assignment_mode:
            ((program as any).crm_assignment_mode ?? null) as
              | 'fixed_seller'
              | 'queue'
              | null,
        },
      });
    } else {
      setForm(emptyState);
    }
  }, [open, program]);

  const isEdit = !!program?.id;
  const saving = createMutation.isPending || updateMutation.isPending;

  const handleNameChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      name: value,
      // Auto-sugere slug apenas em criação ou se ainda estiver vazio
      slug: !isEdit && (!prev.slug || prev.slug === slugify(prev.name))
        ? slugify(value)
        : prev.slug,
    }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.slug.trim()) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Informe nome e slug do programa.',
        variant: 'destructive',
      });
      return;
    }

    // Preserva integralmente o JSONB `settings` existente. O branding agora
    // é gerenciado em Configurações › Indicação (RPC `update_affiliate_program_branding`).
    const mergedSettings: Record<string, any> = {
      ...(form.settings_extra ?? {}),
    };

    const payload = {
      name: form.name.trim(),
      slug: form.slug.trim(),
      description: form.description.trim() || null,
      program_type: form.program_type,
      default_reward_type: form.default_reward_type,
      default_reward_value: Number(form.default_reward_value) || 0,
      cookie_duration_days: Number(form.cookie_duration_days) || 90,
      min_payout_amount: Number(form.min_payout_amount) || 0,
      auto_approve_affiliates: form.auto_approve_affiliates,
      auto_approve_commissions: form.auto_approve_commissions,
      is_active: form.is_active,
      // ----- Branding & Termos (v1.1.0 + v2.1.0) -----
      // terms_html mantido como fallback legado (não editado mais pela UI).
      terms_html: form.terms_html.trim() || null,
      terms_template_id: form.terms_template_id || null,
      require_terms_acceptance: form.require_terms_acceptance,
      settings: mergedSettings,
      // ----- Configuração CRM (Etapa 4) -----
      auto_create_contact: true, // sempre true — evita indicações órfãs
      auto_create_deal: form.crm.auto_create_deal,
      crm_workspace_id: form.crm.crm_workspace_id,
      crm_pipeline_id: form.crm.crm_pipeline_id,
      crm_phase_id: form.crm.crm_phase_id,
      crm_source: form.crm.crm_source,
      crm_default_seller_id: form.crm.crm_default_seller_id,
      crm_distribution_queue_id: form.crm.crm_distribution_queue_id,
      crm_default_tags: form.crm.crm_default_tags,
      crm_deal_title_template: form.crm.crm_deal_title_template,
      crm_assignment_mode: form.crm.crm_assignment_mode, // v1.3.0
    };

    try {
      if (isEdit && program) {
        await updateMutation.mutateAsync({ id: program.id, patch: payload as any });
        toast({ title: 'Programa atualizado com sucesso.' });
      } else {
        await createMutation.mutateAsync(payload as any);
        toast({ title: 'Programa criado com sucesso.' });
      }
      onOpenChange(false);
    } catch (err: any) {
      toast({
        title: 'Erro ao salvar programa',
        description: err?.message ?? 'Tente novamente.',
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      {/* v1.4.0 — Shell premium padronizado (Geral · Termos).
          O ProgramPreviewBridge fica como sibling para evitar conflitos
          de stacking entre dois Dialogs aninhados. */}
      <FormModalShell<'general' | 'branding'>
        open={open}
        onOpenChange={onOpenChange}
        icon={Award}
        title={isEdit ? 'Editar programa' : 'Novo programa'}
        description="Configure as regras gerais, integração com CRM e termos do programa."
        tabs={PROGRAM_MODAL_TABS}
        activeTab={tab}
        onTabChange={setTab}
        onSubmit={handleSubmit}
        isSubmitting={saving}
        submitLabel={isEdit ? 'Salvar alterações' : 'Criar programa'}
        submitIcon={isEdit ? Check : Plus}
        maxWidth="max-w-3xl"
      >
        <div className="grid gap-4">
          {/* ============================ Aba GERAL ============================ */}
          {tab === 'general' && (
            <>
            <div className="grid gap-2">
              <Label htmlFor="name">Nome *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Ex.: Programa Parceiros 2026"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="slug">Slug *</Label>
              <Input
                id="slug"
                value={form.slug}
                onChange={(e) =>
                  setForm((p) => ({ ...p, slug: slugify(e.target.value) }))
                }
                placeholder="parceiros-2026"
              />
              <p className="text-xs text-muted-foreground">
                Identificador único do programa (usado nas URLs públicas).
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="desc">Descrição</Label>
              <Textarea
                id="desc"
                rows={3}
                value={form.description}
                onChange={(e) =>
                  setForm((p) => ({ ...p, description: e.target.value }))
                }
                placeholder="Resumo do programa exibido aos afiliados."
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Tipo do programa</Label>
                <Select
                  value={form.program_type}
                  onValueChange={(v: FormState['program_type']) =>
                    setForm((p) => ({ ...p, program_type: v }))
                  }
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="invite_only">Privado (apenas convite)</SelectItem>
                    <SelectItem value="open">Público (cadastro aberto)</SelectItem>
                    <SelectItem value="internal">Interno (somente equipe)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label>Tipo de recompensa padrão</Label>
                <Select
                  value={form.default_reward_type}
                  onValueChange={(v: FormState['default_reward_type']) =>
                    setForm((p) => ({ ...p, default_reward_type: v }))
                  }
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Comissão (%)</SelectItem>
                    <SelectItem value="fixed">Valor fixo (R$)</SelectItem>
                    {/* Opções ocultas até implementação/validação completa:
                        points / recurring / tiered. Mantidas no type union
                        do FormState para compatibilidade com dados legados. */}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="grid gap-2">
                <Label htmlFor="reward">Valor padrão</Label>
                <Input
                  id="reward"
                  type="number"
                  step="0.01"
                  value={form.default_reward_value}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, default_reward_value: e.target.value }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="cookie">Cookie (dias)</Label>
                <Input
                  id="cookie"
                  type="number"
                  value={form.cookie_duration_days}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, cookie_duration_days: e.target.value }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="payout">Saque mínimo (R$)</Label>
                <Input
                  id="payout"
                  type="number"
                  step="0.01"
                  value={form.min_payout_amount}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, min_payout_amount: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="space-y-3 rounded-lg border p-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Aprovar afiliados automaticamente</Label>
                  <p className="text-xs text-muted-foreground">
                    Novos cadastros entram já como ativos.
                  </p>
                </div>
                <Switch
                  checked={form.auto_approve_affiliates}
                  onCheckedChange={(v) =>
                    setForm((p) => ({ ...p, auto_approve_affiliates: v }))
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Aprovar comissões automaticamente</Label>
                  <p className="text-xs text-muted-foreground">
                    Comissões geradas já entram como aprovadas.
                  </p>
                </div>
                <Switch
                  checked={form.auto_approve_commissions}
                  onCheckedChange={(v) =>
                    setForm((p) => ({ ...p, auto_approve_commissions: v }))
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Programa ativo</Label>
                  <p className="text-xs text-muted-foreground">
                    Quando inativo, novos cliques e cadastros são bloqueados.
                  </p>
                </div>
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(v) =>
                    setForm((p) => ({ ...p, is_active: v }))
                  }
                />
              </div>
            </div>

            {/* Integração CRM (Etapa 4) */}
            <ReferralCrmIntegrationSection
              form={form.crm}
              onChange={(updates) =>
                setForm((p) => ({ ...p, crm: { ...p.crm, ...updates } }))
              }
            />
            </>
          )}

          {/* =========================== Aba TERMOS ============================
              v1.2.0 — Branding (cor + logo) foi removido daqui e centralizado
              em Configurações › Indicação. Esta aba mantém apenas Termos do
              Programa (modelo de contrato vinculado) e exigência de aceite. */}
          {tab === 'branding' && (
            <>
            {/* ============================================================
                v2.1.0 — Termos do Programa via Modelo de Contrato
                Substitui o antigo Textarea livre por seleção de um modelo
                já cadastrado em CRM › Modelos de Contrato. Mantém o aceite
                obrigatório e o switch de exigência inalterados.
               ============================================================ */}
            <ProgramTermsTemplateBlock
              value={form.terms_template_id}
              onChange={(v) => setForm((p) => ({ ...p, terms_template_id: v }))}
              templates={contractTemplates as any[]}
              loadingTemplates={loadingTemplates}
              onPreview={() => setPreviewOpen(true)}
              hasLegacyHtml={!!form.terms_html.trim()}
            />

            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <Label>Exigir aceite obrigatório no cadastro</Label>
                <p className="text-xs text-muted-foreground">
                  O afiliado precisa marcar o aceite antes de finalizar o cadastro.
                </p>
              </div>
              <Switch
                checked={form.require_terms_acceptance}
                onCheckedChange={(v) =>
                  setForm((p) => ({ ...p, require_terms_acceptance: v }))
                }
              />
            </div>
            </>
          )}
        </div>
      </FormModalShell>

      {/* Preview do modelo selecionado (Dialog independente, só abre via botão
          "Visualizar"). Mantido fora do shell para não aninhar dois Dialogs. */}
      <ProgramPreviewBridge
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        templateId={form.terms_template_id}
        templates={contractTemplates as any[]}
        programName={form.name}
      />
    </>
  );
}

// =============================================================================
// Subcomponente: bloco de seleção do Modelo de Contrato (Termos do Programa)
// Espelha TermsTemplateSelector usado em ReferralCampaignFormModal.
// =============================================================================
function ProgramTermsTemplateBlock({
  value,
  onChange,
  templates,
  loadingTemplates,
  onPreview,
  hasLegacyHtml,
}: {
  value: string;
  onChange: (v: string) => void;
  templates: any[];
  loadingTemplates: boolean;
  onPreview: () => void;
  hasLegacyHtml: boolean;
}) {
  // Prioriza modelos da categoria de indicação/affiliate, mas mostra todos.
  const sorted = useMemo(() => {
    return [...(templates ?? [])].sort((a: any, b: any) => {
      const isAffA = ['affiliate', 'referral', 'indicacao'].includes(
        (a.category ?? '').toLowerCase(),
      );
      const isAffB = ['affiliate', 'referral', 'indicacao'].includes(
        (b.category ?? '').toLowerCase(),
      );
      if (isAffA && !isAffB) return -1;
      if (!isAffA && isAffB) return 1;
      return (a.name ?? '').localeCompare(b.name ?? '');
    });
  }, [templates]);

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
      <div className="text-sm font-semibold text-foreground">
        Termos do Programa (Modelo de Contrato)
      </div>
      <p className="text-xs text-muted-foreground">
        Vincule um modelo de contrato com os termos de adesão. O afiliado verá
        o conteúdo durante o cadastro. Reaproveita os modelos cadastrados em
        CRM › Modelos de Contrato.
      </p>
      <div className="grid gap-2">
        <Label>Modelo de contrato (Termos)</Label>
        <div className="flex gap-2">
          <Select
            value={value || '__none__'}
            onValueChange={(v) => onChange(v === '__none__' ? '' : v)}
            disabled={loadingTemplates}
          >
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Sem termos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Sem termos</SelectItem>
              {sorted.map((t: any) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                  {t.category ? ` · ${t.category}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onPreview}
            disabled={!value}
            title="Pré-visualizar termos"
          >
            <Eye className="h-4 w-4 mr-2" />
            Visualizar
          </Button>
        </div>
        {hasLegacyHtml && !value && (
          <p className="text-[11px] text-amber-600 dark:text-amber-400">
            Este programa ainda usa termos antigos em texto livre. Selecione um
            modelo de contrato para migrar para o novo padrão.
          </p>
        )}
        <p className="text-[11px] text-muted-foreground">
          Não encontrou? Crie um novo modelo em Indicação › Modelos de Contrato.
        </p>
      </div>
    </div>
  );
}

// =============================================================================
// Subcomponente: bridge para o ProgramTermsDialog
// Monta um `prefetched` a partir do template já carregado em memória,
// evitando uma RPC adicional só para o preview do admin.
// =============================================================================
function ProgramPreviewBridge({
  open,
  onOpenChange,
  templateId,
  templates,
  programName,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  templateId: string;
  templates: any[];
  programName: string;
}) {
  const prefetched: ProgramTermsData | null = useMemo(() => {
    if (!templateId) return null;
    const t = templates.find((x: any) => x.id === templateId);
    if (!t) return null;
    const rawPos = (t.base_pdf_position ?? 'before') as string;
    return {
      template_id: t.id,
      template_name: t.name ?? 'Termos',
      content_html: t.content_html ?? '',
      version: Number(t.version ?? 1),
      background_image_path: t.background_image_path ?? null,
      show_page_numbers: !!t.show_page_numbers,
      page_number_position: t.page_number_position ?? null,
      bg_margin_top_mm: t.bg_margin_top_mm ?? null,
      bg_margin_bottom_mm: t.bg_margin_bottom_mm ?? null,
      pagination_y_offset_mm: t.pagination_y_offset_mm ?? null,
      cover_pdf_path: t.cover_pdf_path ?? null,
      base_pdf_path: t.base_pdf_path ?? null,
      base_pdf_position: rawPos === 'after' ? 'after' : 'before',
    };
  }, [templateId, templates]);

  return (
    <ProgramTermsDialog
      open={open}
      onOpenChange={onOpenChange}
      prefetched={prefetched}
      programName={programName || null}
    />
  );
}
